export class ApiError extends Error {
    constructor(
        message: string,
        readonly status: number,
    ) {
        super(message);
    }
}

/*
 * Signing in from ServiceM8's menu item. Inside ServiceM8 the app runs in a frame,
 * where browsers block our session cookie as a third-party cookie. So the sign-in link hands the
 * session token over in the URL fragment (#sm8=...): we keep it for this tab and send it ourselves
 * (Authorization header; ?t= on image/download URLs, which can't send headers). Normal sign-in
 * uses the cookie and none of this applies.
 */
const KEY = "sm8-session";
const VIEW_KEY = "sm8-view";
let sessionToken: string | null = null;
/** Opened from ServiceM8's phone app (#...&view=phone): show the simple phone version (src/web/phone). */
let phone = false;
{
    const handedOver = /(?:^#|&)sm8=([^&]+)/.exec(location.hash)?.[1];
    const view = handedOver ? (/(?:^#|&)view=([^&]+)/.exec(location.hash)?.[1] ?? "full") : null;
    try {
        if (handedOver) sessionStorage.setItem(KEY, handedOver);
        if (view) sessionStorage.setItem(VIEW_KEY, view);
        sessionToken = handedOver ?? sessionStorage.getItem(KEY);
        phone = (view ?? sessionStorage.getItem(VIEW_KEY)) === "phone";
    } catch {
        // Storage blocked: keep them in memory for this page.
        sessionToken = handedOver ?? null;
        phone = view === "phone";
    }
    // Out of the address bar (and history) straight away.
    if (handedOver) history.replaceState(history.state, "", location.pathname + location.search);
}

/** True when opened from ServiceM8's phone app, which gets the simple phone version. */
export const phoneView = phone;

function forgetSessionToken() {
    sessionToken = null;
    try {
        sessionStorage.removeItem(KEY);
    } catch {
        // nothing stored
    }
}

/** Headers that carry the ServiceM8 session (empty when using the cookie). */
export const sessionHeaders = (): Record<string, string> =>
    sessionToken ? { authorization: `Bearer ${sessionToken}` } : {};

/** An /api URL for an <img> or download link, carrying the ServiceM8 session if there is one. */
export function withSession(url: string) {
    if (!sessionToken) return url;
    const join = url.includes("?") ? "&" : "?";
    return `${url}${join}t=${encodeURIComponent(sessionToken)}`;
}

/**
 * This page for a new browser tab, signed in as here: inside ServiceM8 the session goes with it in
 * the fragment, as the menu item hands it over (not sent to the server, and out of the address bar
 * as soon as it's read); with the cookie, just the page. Only for opening: to share, copy the
 * plain address.
 */
export function signedInUrl() {
    const url = location.href.split("#")[0] ?? location.href;
    return sessionToken ? `${url}#sm8=${encodeURIComponent(sessionToken)}` : url;
}

/** True when shown inside ServiceM8 (or another frame), where browsers block downloads. */
export const inFrame = (() => {
    try {
        return globalThis.top !== globalThis.self;
    } catch {
        return true;
    }
})();

/** JSON fetch against our own API. Throws ApiError with the server's message on failure. */
export async function api<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
    const usedToken = sessionToken;
    const res = await fetch(`/api${path}`, {
        method: init.method ?? (init.body ? "POST" : "GET"),
        headers: { ...(init.body ? { "content-type": "application/json" } : {}), ...sessionHeaders() },
        body: init.body ? JSON.stringify(init.body) : undefined,
        credentials: "same-origin",
    });
    // A handed-over session that has expired: drop it and try again with the cookie, if any.
    if (res.status === 401 && usedToken) {
        forgetSessionToken();
        return api<T>(path, init);
    }
    if (path === "/auth/logout") forgetSessionToken();
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        // A proxy's own error page (not our JSON) has no message, and over HTTP/2 statusText is empty: never show a blank error.
        const message =
            (data as { error?: string }).error ||
            (data as { message?: string }).message ||
            `Something went wrong (error ${res.status}). Please try again.`;
        throw new ApiError(message, res.status);
    }
    return data as T;
}
